import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Hls from "hls.js";
import api from "../../../api/axios";

/* ─── Brand palette ──────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D", stone: "#4C5C46",
};

const MILL_TYPE_CHIP = {
  flour:   { bg: BRAND.cream, color: BRAND.gold,  border: BRAND.honey, label: "Flour" },
  masala:  { bg: "#E4F0EA",   color: BRAND.pine,  border: "#9CC7B4",   label: "Masala" },
  general: { bg: "#EFF1ED",   color: BRAND.stone, border: "#C9D1C4",   label: "General" },
};

/* Convert any YouTube URL form to its /embed/ form */
const toYouTubeEmbed = (url) => {
  try {
    const u = new URL(url);
    if (u.pathname.includes("/embed/")) return url;
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace(/^\//, "").split("/")[0];
      return id ? `https://www.youtube.com/embed/${id}` : url;
    }
    const v = u.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    /* /live/<id> or /shorts/<id> forms */
    const m = u.pathname.match(/\/(live|shorts)\/([^/?]+)/);
    if (m) return `https://www.youtube.com/embed/${m[2]}`;
    return url;
  } catch (e) {
    return url;
  }
};

const isYouTubeUrl = (url = "") =>
  url.includes("youtube") || url.includes("youtu.be") || url.includes("/embed");

const isHlsUrl = (url = "") => /\.m3u8(\?.*)?$/i.test(url);

const isDirectFileUrl = (url = "") =>
  url.startsWith("http") && /\.(mp4|webm|ogg|ogv|mov)(\?.*)?$/i.test(url);

/* "<base>/<path>/index.m3u8" with slashes normalised */
const joinHlsUrl = (base, path) =>
  `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(/^\/+/, "")}/index.m3u8`;

const formatTime = (d) => {
  try {
    return new Date(d).toLocaleTimeString([], { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return "";
  }
};

const CameraOffIcon = ({ size = 34, color = "var(--mly-text-soft)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const LiveBadge = () => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "#FEE2E2", color: "#B91C1C", border: "1px solid #FCA5A5",
    fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
    padding: "3px 12px", borderRadius: 20, whiteSpace: "nowrap", flexShrink: 0,
  }}>
    <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#DC2626" }} />
    LIVE
  </span>
);

/* ─── HLS playback (hls.js for Chrome/Firefox/Edge, native for Safari) ── */
const HlsPlayer = ({ src, title }) => {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    let hls = null;
    let onNativeError = null;

    if (Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data && data.fatal) {
          if (hls) {
            hls.destroy();
            hls = null;
          }
          setFailed(true);
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      /* Safari plays HLS natively */
      video.src = src;
      onNativeError = () => setFailed(true);
      video.addEventListener("error", onNativeError);
    } else {
      setFailed(true);
    }

    return () => {
      if (hls) hls.destroy();
      if (onNativeError) video.removeEventListener("error", onNativeError);
      video.removeAttribute("src");
      video.load();
    };
  }, [src, attempt]);

  return (
    <div style={{ position: "absolute", inset: 0, background: "#000" }}>
      {/* the <video> stays mounted so retry can re-attach to the same node */}
      <video
        ref={videoRef}
        muted
        autoPlay
        playsInline
        controls
        title={title}
        style={{
          width: "100%", height: "100%", objectFit: "contain",
          display: failed ? "none" : "block",
        }}
      />
      {failed && (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 10,
          background: "var(--mly-surface-3)",
        }}>
          <CameraOffIcon />
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--mly-text-soft)" }}>
            Stream offline
          </span>
          <button
            type="button"
            onClick={() => {
              setFailed(false);
              setAttempt((a) => a + 1);
            }}
            style={{
              padding: "7px 20px", borderRadius: 20, border: "none",
              background: "var(--mly-heading)", color: "var(--mly-on-accent)",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}
    </div>
  );
};

/* ─── Per-order phone-camera stream tile ─────────────────────── */
const OrderStreamTile = ({ order, hlsBase }) => {
  const src = joinHlsUrl(hlsBase, order.liveStreamPath);
  const customer = order.customerName || order.shipName || "Customer";

  return (
    <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
        <HlsPlayer src={src} title={`Order ${order.orderNo || ""}`} />
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, padding: "12px 16px",
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontWeight: 700, fontSize: 13, color: "var(--mly-text)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {order.orderNo || "Order"}
          </div>
          <div style={{
            fontSize: 12, color: "var(--mly-text-soft)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {customer}
            {order.liveStreamStartedAt
              ? ` · started ${formatTime(order.liveStreamStartedAt)}`
              : ""}
          </div>
        </div>
        <LiveBadge />
      </div>
    </div>
  );
};

/* ─── Configured-camera tile ─────────────────────────────────── */
const CameraTile = ({ camera }) => {
  const chip = MILL_TYPE_CHIP[camera.millType] || MILL_TYPE_CHIP.general;
  const url  = (camera.url || "").trim();
  const live = camera.isLive === true;

  let media;
  if (url && isYouTubeUrl(url)) {
    media = (
      <iframe
        title={camera.name || "Camera"}
        src={toYouTubeEmbed(url)}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
      />
    );
  } else if (url && isHlsUrl(url)) {
    media = <HlsPlayer src={url} title={camera.name || "Camera"} />;
  } else if (url && isDirectFileUrl(url)) {
    media = (
      <video
        controls
        autoPlay
        muted
        playsInline
        src={url}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
      />
    );
  } else {
    media = (
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 10,
        background: "var(--mly-surface-2)",
      }}>
        <CameraOffIcon />
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "8px 18px", borderRadius: 20,
              background: "var(--mly-heading)", color: "var(--mly-on-accent)",
              fontSize: 12, fontWeight: 700, textDecoration: "none",
            }}
          >
            Open stream ↗
          </a>
        ) : (
          <span style={{ fontSize: 12, color: "var(--mly-text-soft)", fontWeight: 600 }}>No stream URL set</span>
        )}
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
      {/* 16:9 media area */}
      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", background: "#000" }}>
        {media}
      </div>
      {/* Tile footer */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, padding: "12px 16px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
            background: live ? "#22c55e" : "var(--mly-text-muted)",
            boxShadow: live ? "0 0 0 3px #dcfce7" : "0 0 0 3px var(--mly-surface-3)",
          }} />
          <span style={{
            fontWeight: 700, fontSize: 13, color: "var(--mly-text)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {camera.name || "Camera"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span style={{
            background: live ? "#FEE2E2" : "var(--mly-surface-3)",
            color: live ? "#B91C1C" : "var(--mly-text-muted)",
            border: `1px solid ${live ? "#FCA5A5" : "var(--mly-border)"}`,
            fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
            padding: "3px 12px", borderRadius: 20, whiteSpace: "nowrap",
          }}>
            {live ? "LIVE" : "OFF"}
          </span>
          <span style={{
            background: chip.bg, color: chip.color,
            border: `1px solid ${chip.border}`,
            fontSize: 11, fontWeight: 700,
            padding: "3px 12px", borderRadius: 20, whiteSpace: "nowrap",
          }}>
            {chip.label}
          </span>
        </div>
      </div>
    </div>
  );
};

const SectionHeading = ({ title, note }) => (
  <div style={{
    display: "flex", alignItems: "baseline", justifyContent: "space-between",
    gap: 10, marginBottom: 14,
  }}>
    <h6 style={{ margin: 0, fontWeight: 800, color: "var(--mly-heading)", fontSize: 14 }}>
      {title}
    </h6>
    {note ? <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>{note}</span> : null}
  </div>
);

const GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
  gap: 20,
};

/* ── Scope picker — same pills as /admin/store/settings ────────
   Cameras are one of the per-store overridable settings, so the store being
   watched has to be chosen here too; the styling is lifted from Settings.jsx
   so switching between the two pages feels like one screen. */
const SCOPE_PILL = (active) => ({
  display: "inline-flex", alignItems: "center", gap: 7,
  padding: "7px 14px", borderRadius: 999, cursor: "pointer",
  border: `1.5px solid ${active ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
  background: active ? "var(--milliyo-forest)" : "var(--mly-surface)",
  color: active ? "#E8C96A" : "var(--mly-text-soft)",
  fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
});

const CHIP = {
  fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase",
  padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
};

const CHIP_INHERITED  = { ...CHIP, background: "var(--mly-surface-3)", color: "var(--mly-text-muted)", border: "1px solid var(--mly-border)" };
const CHIP_OVERRIDDEN = { ...CHIP, background: "var(--mly-accent-soft)", color: "var(--mly-heading)", border: "1px solid var(--mly-border)" };

const ORDERS_POLL_MS = 15000;

const LiveView = () => {
  const [cameras, setCameras]       = useState([]);
  const [hlsBase, setHlsBase]       = useState("");
  const [liveOrders, setLiveOrders] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error,   setError]         = useState("");

  /* scope: null = the estate defaults, otherwise the store being watched.
     Cameras are an overridable setting, so the global row is only one of the
     answers — a store that pins its own list has to be asked for by id. */
  const [stores,     setStores]     = useState([]);
  const [storeId,    setStoreId]    = useState(null);
  /* whether the resolved cameras are this store's own list or the inherited one */
  const [ownCameras, setOwnCameras] = useState(false);
  /* switching scope reloads the cameras only — the order streams keep playing */
  const [switching,  setSwitching]  = useState(false);

  const storeMode = !!storeId;
  const activeStore = stores.find((s) => String(s.storeId) === String(storeId));

  /* clicking through scopes leaves several loads in flight at once, and they
     do not have to come back in order — only the newest may write, or a slow
     reply lands one store's cameras under the name of the store the operator
     has already moved on to */
  const scopeSeq = useRef(0);

  const loadCameras = useCallback(async (id) => {
    const seq = scopeSeq.current + 1;
    scopeSeq.current = seq;
    try {
      const res = await api.get(
        id
          ? `/admin/store-settings/resolved?storeId=${encodeURIComponent(id)}`
          : "/admin/store-settings"
      );
      if (seq !== scopeSeq.current) return false;
      /* the settings payload IS the body now — no { data } envelope, and
         _meta rides inside it rather than beside it */
      const s = res.data || {};
      const meta = s._meta || {};
      /* TODO(cutover): server does not return cameras. There is no camera
         column, table or endpoint anywhere in the service, so this list is
         permanently empty and the Mill cameras grid below shows its empty
         state. Left in place rather than deleted so the grid comes back the
         day the column lands. */
      const list = Array.isArray(s.cameras) ? s.cameras : [];
      setCameras(list.filter((c) => c.enabled !== false));
      /* survives with the same name and meaning: hlsBaseUrl, or the server's
         HLS_BASE_URL fallback */
      setHlsBase(s.streamHlsBase || "");
      /* TODO(cutover): overriddenFields can only ever name the two per-mill
         fee columns, so this is always false and the chip always reads
         "Inherited cameras". */
      setOwnCameras(
        Array.isArray(meta.overriddenFields) && meta.overriddenFields.includes("cameras")
      );
      setError("");
    } catch (err) {
      if (seq !== scopeSeq.current) return false;
      /* a scope whose load failed must not leave the previous store's tiles on
         screen under the new store's name. hlsBase is deliberately kept: it is
         the HLS origin the ORDER streams play from, not per-store camera data,
         and blanking it would knock every live order tile offline because a
         camera list failed to load. */
      setCameras([]);
      setOwnCameras(false);
      setError(err.response?.data?.message || "Failed to load cameras");
    }
    return true;
  }, []);

  useEffect(() => {
    let cancelled = false;

    /* the server already narrows this to what the caller may watch. An estate
       whose API predates the endpoint answers nothing, and the page falls back
       to the estate-wide defaults — the same row it has always read, and one
       that carries no store's overrides, so a missing list degrades the page
       rather than widening it. */
    const loadScopes = async () => {
      try {
        const res = await api.get("/admin/store-settings/overview");
        return Array.isArray(res.data?.data) ? res.data.data : [];
      } catch (err) {
        return [];
      }
    };

    const loadLiveOrders = async () => {
      try {
        /* TODO(cutover): no server endpoint for this yet. The list keeps its
           { data, pagination } wrapper and its status/limit params, but no
           LIST endpoint returns liveStreamPath / liveStreamStartedAt — only
           GET /admin/orders/{orderNo} does, which would mean fetching every
           order's detail on every poll. The filter below is therefore always
           empty and the "Live order streams" grid never shows a tile. */
        const res = await api.get("/admin/orders", {
          params: { status: "assigned,preparing", limit: 100 },
        });
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        if (cancelled) return;
        setLiveOrders(rows.filter((o) => (o.liveStreamPath || "").trim() !== ""));
      } catch (err) {
        /* transient poll failure — keep the last known streams on screen */
      }
    };

    (async () => {
      const list = await loadScopes();
      if (cancelled) return;
      setStores(list);
      /* stores-overview is already scoped by the server: the whole estate for a
         superadmin, exactly its own store for anyone else. A list of one is
         therefore the only store this operator can watch, default or not, so
         open on it. Asking for the store by id is what picks up an override —
         the global row would otherwise show the estate default under a store
         that pins a camera list of its own. */
      const pinned = list.length === 1 ? list[0].storeId : null;
      setStoreId(pinned);
      await Promise.all([loadCameras(pinned), loadLiveOrders()]);
      if (!cancelled) setLoading(false);
    })();

    /* millers start/stop from the camera app — poll so tiles appear/disappear */
    const timer = setInterval(loadLiveOrders, ORDERS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [loadCameras]);

  const selectScope = async (id) => {
    if (String(id || "") === String(storeId || "")) return;
    setStoreId(id);
    setSwitching(true);
    /* an overtaken load leaves the flag alone — the scope that overtook it
       owns clearing it, so a stale reply cannot reveal the wrong tiles */
    if (await loadCameras(id)) setSwitching(false);
  };

  const multiStore = stores.length > 1;

  /* the scope is worth naming when more than one is reachable, or when the
     store pinned to this operator is not the estate default; a lone default
     store reads exactly as it did before the picker existed */
  const scopeName =
    storeMode && (multiStore || activeStore?.isDefault === false)
      ? activeStore?.name || ""
      : "";

  return (
    <div>
      <div className="container-fluid">
        <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 style={{ margin: 0 }}>Live View</h5>
            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
              {liveOrders.length} order stream{liveOrders.length !== 1 ? "s" : ""}
              {" · "}
              {/* the old count belongs to the old scope — do not show it under
                  the new one while the switch is still in flight */}
              {switching
                ? "loading cameras"
                : `${cameras.length} camera${cameras.length !== 1 ? "s" : ""}`}
            </span>
          </div>

          <div className="card-body">
            {/* ── Scope selector — hidden entirely on a single-store estate ── */}
            {multiStore && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                  <button type="button" onClick={() => selectScope(null)} style={SCOPE_PILL(!storeMode)}>
                    All stores (default)
                  </button>
                  {stores.map((s) => {
                    const active = String(s.storeId) === String(storeId);
                    return (
                      <button key={s.storeId} type="button" onClick={() => selectScope(s.storeId)} style={SCOPE_PILL(active)}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                  fontSize: 11, color: "var(--mly-text-muted)",
                }}>
                  {storeMode ? (
                    <>
                      <span style={ownCameras ? CHIP_OVERRIDDEN : CHIP_INHERITED}>
                        {ownCameras ? "Own cameras" : "Inherited cameras"}
                      </span>
                      <span>
                        Watching <strong style={{ color: "var(--mly-text-soft)" }}>{activeStore?.name || "this store"}</strong>.
                        {ownCameras
                          ? " This store has its own camera list."
                          : " This store follows the estate defaults — override its cameras on Store Settings."}
                      </span>
                    </>
                  ) : (
                    <span>These are the estate-wide default cameras — every store shows these unless it overrides them.</span>
                  )}
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : (
              <>
                {/* ── Per-order phone-camera streams ── */}
                <div style={{ marginBottom: 32 }}>
                  <SectionHeading
                    title="Live order streams"
                    note="refreshes every 15s"
                  />
                  {liveOrders.length === 0 ? (
                    <div style={{
                      background: "var(--mly-surface-2)",
                      border: `1px dashed ${BRAND.honey}`,
                      borderRadius: 12,
                      padding: "30px 16px",
                      display: "flex", flexDirection: "column",
                      alignItems: "center", gap: 8,
                    }}>
                      <CameraOffIcon size={28} color="var(--mly-text-muted)" />
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text-soft)", textAlign: "center" }}>
                        No millers are streaming right now — streams appear here
                        the moment a miller goes live from the camera app.
                      </span>
                    </div>
                  ) : (
                    <div style={GRID_STYLE}>
                      {liveOrders.map((order) => (
                        <OrderStreamTile
                          key={order.orderNo}
                          order={order}
                          hlsBase={hlsBase}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Configured mill cameras ── */}
                <div>
                  <SectionHeading title="Mill cameras" note={scopeName} />
                  {/* a camera load that failed is reported HERE and not over the
                      whole page: the order streams above come from a different
                      call and must keep playing through it */}
                  {switching ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : error ? (
                    <div className="text-danger text-center py-4">{error}</div>
                  ) : cameras.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "48px 0" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
                        <CameraOffIcon size={48} color="var(--mly-text-muted)" />
                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mly-text-muted)" }}>
                          {scopeName
                            ? `No cameras for ${scopeName} — add them in Store Settings.`
                            : "No cameras configured — add them in Store Settings."}
                        </span>
                        <Link
                          to="/admin/store/settings"
                          style={{
                            padding: "9px 20px", borderRadius: 10,
                            background: "var(--mly-heading)", color: "var(--mly-on-accent)",
                            fontSize: 13, fontWeight: 700, textDecoration: "none",
                          }}
                        >
                          Open Store Settings
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div style={GRID_STYLE}>
                      {/* the scope is part of the key so a switch mounts fresh
                          players — a tile reused across stores would carry the
                          previous stream's offline state with it */}
                      {cameras.map((cam, i) => (
                        <CameraTile
                          key={`${storeId || "global"}-${cam._id || `${cam.name}-${i}`}`}
                          camera={cam}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveView;
